import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { SelectField } from '@wk/components-v3-react16';
import { useFileSizeFilter } from './useFileSizeFilter';
import type { FileSizeFilterModel, FilterType } from './types';
import styles from './FileSizeFilter.module.scss';

interface FileSizeFilterProperties
  extends CustomFilterDisplayProps<FileSizeFilterModel>,
    FilterWrapperParams {}

const parseValueFromModel = (value?: number): string => {
  return value === undefined ? '' : value.toString();
};

const FileSizeFilter = ({
  model,
  onStateChange,
  state,
}: FileSizeFilterProperties) => {
  const componentReference = useRef<HTMLDivElement>(null);
  const initialModelSet = useRef(false);

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters?.suppressFocus) {
        componentReference.current?.focus();
      }
    },
    []
  );

  useGridFilterDisplay({ afterGuiAttached });
  useFileSizeFilter(model);

  const defaultFilterType: FilterType = 'greaterThan';

  const [filterType, setFilterType] = useState<FilterType>(
    state.model?.type || defaultFilterType
  );
  const [fileSizeMore, setFileSizeMore] = useState<string>(
    parseValueFromModel(state.model?.fileSizeMore)
  );
  const [fileSizeLess, setFileSizeLess] = useState<string>(
    parseValueFromModel(state.model?.fileSizeLess)
  );

  // Set initial model when filter opens (so Apply works even without interaction)
  useEffect(() => {
    if (!state.model && !initialModelSet.current) {
      const moreValue = Number.parseFloat(fileSizeMore);
      const lessValue = Number.parseFloat(fileSizeLess);
      const hasMore = !Number.isNaN(moreValue) && moreValue >= 0;
      const hasLess = !Number.isNaN(lessValue) && lessValue >= 0;

      if (hasMore || hasLess) {
        const initialModel: FileSizeFilterModel = { type: filterType };
        if (hasMore) {
          initialModel.fileSizeMore = moreValue;
        }
        if (hasLess) {
          initialModel.fileSizeLess = lessValue;
        }
        onStateChange({ model: initialModel });
        initialModelSet.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync state when model changes (for Reset button)
  useEffect(() => {
    if (state.model) {
      setFilterType(state.model.type);
      setFileSizeMore(parseValueFromModel(state.model.fileSizeMore));
      setFileSizeLess(parseValueFromModel(state.model.fileSizeLess));
    } else {
      setFilterType(defaultFilterType);
      setFileSizeMore('');
      setFileSizeLess('');
    }
  }, [state.model]);

  const filterTypeOptions = useMemo(
    () => [
      { label: 'Greater Than', value: 'greaterThan' },
      { label: 'Less Than', value: 'lessThan' },
      { label: 'Between', value: 'inRange' },
    ],
    []
  );

  const buildModel = useCallback(
    (
      type: FilterType,
      moreString: string,
      lessString: string
    ): FileSizeFilterModel | null => {
      const moreValue = Number.parseFloat(moreString);
      const lessValue = Number.parseFloat(lessString);
      const hasMore = !Number.isNaN(moreValue) && moreValue >= 0;
      const hasLess = !Number.isNaN(lessValue) && lessValue >= 0;

      if (type === 'greaterThan') {
        return hasMore ? { fileSizeMore: moreValue, type } : null;
      }
      if (type === 'lessThan') {
        return hasLess ? { fileSizeLess: lessValue, type } : null;
      }
      // inRange
      if (!hasMore && !hasLess) {
        return null;
      }
      const newModel: FileSizeFilterModel = { type };
      if (hasMore) {
        newModel.fileSizeMore = moreValue;
      }
      if (hasLess) {
        newModel.fileSizeLess = lessValue;
      }
      return newModel;
    },
    []
  );

  const handleFilterTypeChange = (newType: FilterType) => {
    setFilterType(newType);
    setFileSizeMore('');
    setFileSizeLess('');
  };

  const handleFileSizeMoreChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
        return;
      }

      setFileSizeMore(value);

      onStateChange({ model: buildModel(filterType, value, fileSizeLess) });
    },
    [filterType, fileSizeLess, buildModel, onStateChange]
  );

  const handleFileSizeLessChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
        return;
      }

      setFileSizeLess(value);

      onStateChange({ model: buildModel(filterType, fileSizeMore, value) });
    },
    [filterType, fileSizeMore, buildModel, onStateChange]
  );

  return (
    <div className={styles.container} ref={componentReference} tabIndex={-1}>
      <div className={styles.filterContent}>
        <div className={styles.filterRow}>
          <div className={styles.filterSelect}>
            <SelectField size="medium">
              <select
                value={filterType}
                onChange={(event) =>
                  handleFilterTypeChange(event.target.value as FilterType)
                }
              >
                {filterTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </SelectField>
          </div>
        </div>

        {/* fileSizeMore input — shown for greaterThan and inRange */}
        {filterType !== 'lessThan' && (
          <div className={styles.inputSection}>
            <div className={styles.inputRow}>
              <div className={styles.megabytesInput}>
                <input
                  type="text"
                  value={fileSizeMore}
                  onChange={handleFileSizeMoreChange}
                  placeholder={
                    filterType === 'inRange'
                      ? 'Enter from # bytes'
                      : 'Enter # bytes'
                  }
                />
              </div>
            </div>
          </div>
        )}

        {/* fileSizeLess input — shown for lessThan and inRange */}
        {filterType !== 'greaterThan' && (
          <div className={styles.inputSection}>
            <div className={styles.inputRow}>
              <div className={styles.megabytesInput}>
                <input
                  type="text"
                  value={fileSizeLess}
                  onChange={handleFileSizeLessChange}
                  placeholder={
                    filterType === 'inRange'
                      ? 'Enter to # bytes'
                      : 'Enter # bytes'
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileSizeFilter;
